import { useQuery } from "@tanstack/react-query";

import { cacheView, getCachedView } from "../services/offline-cache";
import { getView } from "../services/view";
import { useConnection } from "../stores/connection";
import { useViews } from "../stores/views";
import { View } from "../types/view";

export type ViewResult = {
  cachedAt: number | null;
  isFromCache: boolean;
  view: View;
};

function cachedViewToView(cached: Awaited<ReturnType<typeof getCachedView>>): View | null {
  if (!cached) return null;
  return {
    code: cached.code,
    name: cached.name,
    createdAt: new Date(cached.cachedAt),
    updatedAt: new Date(cached.cachedAt),
    images: cached.images.map((img) => ({
      id: img.id,
      bytes: img.bytes,
      title: img.title,
      order: img.order,
    })),
  };
}

export const useView = (code: string, access: "admin" | "user") => {
  const { connection, signin } = useConnection();
  return useQuery({
    queryKey: ["view", code, access],
    queryFn: async (): Promise<ViewResult> => {
      if (!code) {
        throw new Error("Code is required");
      }

      if (!navigator.onLine) {
        const cached = await getCachedView(code);
        const view = cachedViewToView(cached);
        if (view) {
          return { cachedAt: cached!.cachedAt, isFromCache: true, view };
        }
        throw new Error("Offline and no cached data available");
      }

      try {
        await signin(code, access);
        const view = await getView({ connection, code });
        useViews
          .getState()
          .addOrUpdateView({ code: view.code, name: view.name ?? code });
        await cacheView(view);
        return { cachedAt: null, isFromCache: false, view };
      } catch (err) {
        const cached = await getCachedView(code);
        const view = cachedViewToView(cached);
        if (view) {
          return { cachedAt: cached!.cachedAt, isFromCache: true, view };
        }
        throw err;
      }
    },
    retry: false,
    staleTime: Infinity,
  });
};
